import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BottomSheet, type BottomSheetProps } from './BottomSheet';
import { Button } from './Button';
import styles from './ConfirmSheet.module.css';
import { sheetStyles } from './sheetStyles';
import { shake } from '@/ui/motion/motion';

export interface ConfirmSheetProps {
  title: string;
  /**
   * What will happen, in plain words, with the case number and the detail that
   * matters: "Mark JO-2047 as done and send it to Final Check?".
   */
  statement: string;
  /** Anything the decision needs: the destination, the person, a reason. */
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  /**
   * Return false when the sheet should stay as it is, a required reason
   * missing, and the confirm is pressable again at once.
   */
  onConfirm: () => boolean | void;
  onCancel: () => void;
  /** The command is in flight. The confirm stays locked until it settles. */
  busy?: boolean;
  confirmDisabled?: boolean;
  unsaved?: BottomSheetProps['unsaved'];
}

/**
 * The one confirmation before a case action: Mark done, Flag, Take and Assign.
 * Request of 4 October 2026 (second), section 1.
 *
 * And, since the request of 5 October 2026 (third), section 2, before every
 * action by the accountant, the admin and the receptionist: `ConfirmHost`
 * draws it for a one-tap action, and a dialog that collects input is built on
 * it rather than asking a second question on top.
 *
 * A bottom sheet on a phone and a centred panel on a desk, as every sheet is.
 * The statement says what will happen in words a person reads once and is
 * sure of; the confirm is the primary button and locks on the first tap, so a
 * double tap on a slow connection cannot send the command twice. A refusal
 * releases it, so the same sheet can try again.
 */
export function ConfirmSheet({
  title,
  statement,
  children,
  confirmLabel,
  cancelLabel,
  closeLabel,
  onConfirm,
  onCancel,
  busy = false,
  confirmDisabled = false,
  unsaved = null,
}: ConfirmSheetProps) {
  const [pressed, setPressed] = useState(false);
  const wasBusy = useRef(busy);

  // The command settled without the sheet going away: a refusal. Pressable again.
  useEffect(() => {
    if (wasBusy.current && !busy) setPressed(false);
    wasBusy.current = busy;
  }, [busy]);

  const confirmButton = useRef<HTMLButtonElement>(null);

  function confirm() {
    if (pressed || busy) return;
    setPressed(true);
    if (onConfirm() === false) {
      // Refused here, a reason missing: a gentle shake beside the message that says why.
      setPressed(false);
      shake(confirmButton.current);
    }
  }

  const locked = pressed || busy;

  return (
    <BottomSheet
      title={title}
      onClose={onCancel}
      closeLabel={closeLabel}
      unsaved={locked ? null : unsaved}
      footer={
        <div className={sheetStyles.actions}>
          <Button onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            className={sheetStyles.primary}
            ref={confirmButton}
            onClick={confirm}
            disabled={locked || confirmDisabled}
            loading={busy}
            aria-busy={locked}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <p className={styles.statement} data-testid="confirm-statement">
        {statement}
      </p>
      {children}
    </BottomSheet>
  );
}
